import { useEffect, useState } from "react";
import { Sparkles, Check, X, RefreshCw, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "./adminApi";

const FIELD_LABELS = {
  first_name: "Prénom", last_name: "Nom", birth_name: "Nom de naissance",
  civility: "Civilité", email: "Email", phone: "Téléphone",
  address: "Adresse", postal_code: "Code postal", city: "Ville",
  birth_date: "Date de naissance", birth_place: "Lieu de naissance",
  nationality: "Nationalité", marital_status: "Situation familiale",
  social_security_number: "N° sécurité sociale",
  id_doc_type: "Type de pièce", id_doc_validity_start: "Pièce — délivrée le",
  id_doc_validity_end: "Pièce — expire le",
  id_doc_origin: "Pièce — pays d'émission", id_doc_department: "Pièce — département",
  id_doc_issuer: "Pièce — autorité",
  experience: "Expérience pro",
};

const STATUS_LABEL = {
  ok: "✓ Données détectées",
  queued: "⏳ En file d'attente",
  processing: "⚙ Analyse en cours…",
  llm_empty: "ℹ Aucune info détectée",
  ocr_empty: "ℹ OCR vide (documents illisibles)",
  no_documents: "—",
  none: "—",
  applied: "✓ Appliqué",
  dismissed: "✗ Ignoré",
  error: "⚠ Erreur",
  timeout: "⏱ Délai dépassé",
  llm_unavailable: "💸 Crédit IA épuisé",
};
const STATUS_COLOR = {
  ok: "bg-amber-100 text-amber-800 border-amber-300",
  queued: "bg-gray-100 text-gray-600",
  processing: "bg-blue-100 text-blue-700",
  llm_empty: "bg-gray-100 text-gray-500",
  ocr_empty: "bg-gray-100 text-gray-500",
  no_documents: "bg-gray-100 text-gray-500",
  none: "bg-gray-100 text-gray-500",
  applied: "bg-green-100 text-green-800",
  dismissed: "bg-gray-100 text-gray-600",
  error: "bg-red-100 text-red-700",
  timeout: "bg-orange-100 text-orange-800 border-orange-300",
  llm_unavailable: "bg-purple-100 text-purple-700 border-purple-300",
};

export default function AiExtractPanel({ userId, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/candidates/${userId}/ai-extract`);
      setData(r.data);
      // Default selection: only fields currently empty (safer)
      const sel = {};
      (r.data.diff || []).forEach((d) => { if (d.is_empty) sel[d.field] = true; });
      setSelected(sel);
    } catch {
      setData({ status: "none", diff: [], fields: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) fetchData(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const runExtract = async () => {
    setExtracting(true);
    try {
      const r = await api.post(`/admin/candidates/${userId}/ai-extract`);
      setData(r.data);
      const sel = {};
      (r.data.diff || []).forEach((d) => { if (d.is_empty) sel[d.field] = true; });
      setSelected(sel);
      toast.success("Analyse IA terminée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'analyse");
    } finally {
      setExtracting(false);
    }
  };

  const applySelected = async () => {
    const fields = {};
    (data?.diff || []).forEach((d) => {
      if (selected[d.field]) fields[d.field] = d.extracted;
    });
    if (Object.keys(fields).length === 0) { toast.error("Aucun champ coché"); return; }
    setApplying(true);
    try {
      await api.post(`/admin/candidates/${userId}/ai-extract/apply`, { fields });
      toast.success(`${Object.keys(fields).length} champ(s) appliqué(s)`);
      onApplied?.();
      // Refresh to clear the banner
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'application");
    } finally {
      setApplying(false);
    }
  };

  const dismiss = async () => {
    try {
      await api.post(`/admin/candidates/${userId}/ai-extract/dismiss`);
      toast.success("Analyse IA masquée");
      fetchData();
    } catch { toast.error("Erreur"); }
  };

  if (loading) return null;
  if (!data) return null;

  const status = data.status || "none";
  const diff = data.diff || [];
  const sources = data.sources || [];
  const hasData = status === "ok" && diff.length > 0;

  // Hide the panel entirely when nothing useful and never extracted
  if (status === "none" && !data.extracted_at) {
    return (
      <div className="border border-dashed border-[#662483]/40 bg-[#F0E9F3]/30 p-4" data-testid="ai-extract-empty">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#662483]" />
            <span className="text-sm font-bold text-[#662483]">Auto-remplir depuis les pièces (IA)</span>
          </div>
          <Button size="sm" onClick={runExtract} disabled={extracting} className="bg-[#662483] text-white rounded-none text-xs h-7" data-testid="ai-extract-run">
            {extracting ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Analyse…</> : <><Sparkles className="w-3 h-3 mr-1" /> Lancer l'analyse</>}
          </Button>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">L'IA va lire le CV, la pièce d'identité, l'attestation sécu, le RIB… et proposer un pré-remplissage de la fiche.</p>
      </div>
    );
  }

  return (
    <div className={`border-2 ${hasData ? "border-amber-300 bg-amber-50/40" : "border-gray-200 bg-[#FAFAFA]"} p-4`} data-testid="ai-extract-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="w-4 h-4 text-[#662483]" />
          <span className="text-sm font-bold text-[#662483]">Données détectées par IA</span>
          <Badge className={`rounded-none text-[10px] border ${STATUS_COLOR[status] || "bg-gray-100"}`}>
            {STATUS_LABEL[status] || status}
          </Badge>
          {sources.length > 0 && (
            <span className="text-[10px] text-gray-500">sources : {sources.join(", ")}</span>
          )}
          {data.extracted_at && (
            <span className="text-[10px] text-gray-400">· {new Date(data.extracted_at).toLocaleString("fr-FR")}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)} className="h-6 px-2 text-xs">
            {expanded ? <><EyeOff className="w-3 h-3 mr-1" /> Masquer</> : <><Eye className="w-3 h-3 mr-1" /> Afficher</>}
          </Button>
          <Button size="sm" variant="ghost" onClick={runExtract} disabled={extracting} className="h-6 px-2 text-xs" data-testid="ai-extract-rerun" title="Relancer l'analyse">
            <RefreshCw className={`w-3 h-3 ${extracting ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {!expanded && null}

      {expanded && hasData && (
        <>
          <div className="bg-white border border-gray-100 max-h-72 overflow-y-auto divide-y divide-gray-50">
            {diff.map((d) => {
              const checked = !!selected[d.field];
              return (
                <label
                  key={d.field}
                  className={`flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-[#F0E9F3]/30 ${checked ? "bg-[#F0E9F3]/40" : ""}`}
                  data-testid={`ai-extract-row-${d.field}`}
                >
                  <Checkbox checked={checked} onCheckedChange={(v) => setSelected((s) => ({ ...s, [d.field]: !!v }))} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-[#3F3F3F]">{FIELD_LABELS[d.field] || d.field}</p>
                    <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-400 line-through">{d.current || "(vide)"}</span>
                      <span className="text-xs text-gray-300">→</span>
                      <span className="text-xs font-mono text-[#662483] break-all">{d.extracted}</span>
                    </div>
                  </div>
                  {d.is_empty ? (
                    <Badge className="rounded-none text-[9px] bg-green-100 text-green-700 shrink-0">Champ vide</Badge>
                  ) : d.changed ? (
                    <Badge className="rounded-none text-[9px] bg-amber-100 text-amber-700 shrink-0" title="Différent de la valeur actuelle">À vérifier</Badge>
                  ) : (
                    <Badge className="rounded-none text-[9px] bg-gray-100 text-gray-500 shrink-0">Identique</Badge>
                  )}
                </label>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button
              onClick={applySelected}
              disabled={applying}
              className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-8"
              data-testid="ai-extract-apply"
            >
              <Check className="w-3 h-3 mr-1" /> {applying ? "Application..." : `Appliquer la sélection (${Object.values(selected).filter(Boolean).length})`}
            </Button>
            <Button size="sm" variant="outline" onClick={() => {
              const all = {}; diff.forEach((d) => { all[d.field] = true; }); setSelected(all);
            }} className="rounded-none text-xs h-8">Tout cocher</Button>
            <Button size="sm" variant="outline" onClick={() => {
              const empty = {}; diff.forEach((d) => { if (d.is_empty) empty[d.field] = true; }); setSelected(empty);
            }} className="rounded-none text-xs h-8" title="Ne cocher que les champs actuellement vides — plus sûr">
              Champs vides seulement
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelected({})} className="rounded-none text-xs h-8">Décocher tout</Button>
            <Button size="sm" variant="ghost" onClick={dismiss} className="rounded-none text-xs h-8 text-gray-500 ml-auto" data-testid="ai-extract-dismiss">
              <X className="w-3 h-3 mr-1" /> Ignorer
            </Button>
          </div>
        </>
      )}

      {expanded && !hasData && status !== "ok" && (
        <div className="space-y-3">
          <div className="text-xs text-gray-600">
            {status === "llm_empty" && "L'IA n'a pas pu extraire d'informations claires. Vous pouvez relancer l'analyse ou compléter manuellement."}
            {status === "ocr_empty" && "Les documents n'ont pas pu être lus (images de mauvaise qualité). Téléversez de meilleurs scans si possible."}
            {status === "no_documents" && "Aucun document à analyser pour le moment."}
            {status === "processing" && (
              <span className="flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> Analyse en cours, patientez quelques secondes…</span>
            )}
            {status === "queued" && "En attente du traitement IA…"}
            {status === "error" && (
              <span className="flex items-center gap-2"><AlertCircle className="w-3 h-3 text-red-500" /> Une erreur est survenue lors de l'analyse.</span>
            )}
            {status === "timeout" && (
              <span className="flex items-center gap-2"><AlertCircle className="w-3 h-3 text-orange-500" /> L'analyse a dépassé le délai (documents trop lourds). Relancez : le traitement est maintenant plus rapide et limité aux 4 documents les plus utiles.</span>
            )}
            {status === "llm_unavailable" && (
              <span className="flex items-center gap-2"><AlertCircle className="w-3 h-3 text-purple-500" /> Crédits Universal Key épuisés ou rate-limit atteint. Rechargez votre solde puis relancez.</span>
            )}
          </div>
          {(status === "timeout" || status === "error" || status === "llm_empty" || status === "ocr_empty" || status === "llm_unavailable") && (
            <Button
              onClick={runExtract}
              disabled={extracting}
              className="bg-[#662483] text-white rounded-none text-xs h-8"
              data-testid="ai-extract-retry"
            >
              {extracting ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Analyse…</> : <><Sparkles className="w-3 h-3 mr-1" /> Relancer l'analyse</>}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
